import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { fetchSpeakingExercise, saveExerciseProgress, loadExerciseProgress, resetExerciseProgress, recordExerciseAttempt } from '../../services/api';
import { useExerciseTimer } from '../../hooks/useExerciseTimer';
import Loading from '../Loading';
import Error from '../Error';
import SpeakingPanel from './SpeakingPanel';
import ExerciseLayout from '../shared/ExerciseLayout';

const SpeakingExercise = forwardRef(({ topic = 'groceries', dialogueNumber = 1, conversationId = null, onBack }, ref) => {
  const [instruction, setInstruction] = useState('');
  const [questions, setQuestions] = useState([]);
  const [userAnswers, setUserAnswers] = useState({});
  // Per-question Azure pronunciation results, keyed by question id.
  const [pronunciationResults, setPronunciationResults] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { stop: stopTimer } = useExerciseTimer();

  // Expose saveProgress method to parent component
  useImperativeHandle(ref, () => ({
    saveProgress: async () => {
      try {
        await saveExerciseProgress(topic, 'speaking', {
          userAnswers,
          pronunciationResults
        });
      } catch (err) {
        console.error('Error saving progress from ref:', err);
        throw err;
      }
    }
  }));

  // Fetch exercises from API on component mount
  useEffect(() => {
    const loadExercises = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await fetchSpeakingExercise({ topic });

        setInstruction(data.instruction);
        setQuestions(data.questions);

      } catch (err) {
        console.error('Error fetching speaking exercises:', err);
        setError(err.message || 'Failed to load exercises');
      } finally {
        setLoading(false);
      }
    };

    loadExercises();
  }, [topic]);

  // Load saved progress after questions are loaded
  useEffect(() => {
    const loadProgress = async () => {
      if (!loading && questions.length > 0) {
        try {
          const savedProgress = await loadExerciseProgress(topic, 'speaking');
          if (savedProgress && savedProgress.progress_data) {
            if (savedProgress.progress_data.userAnswers) {
              setUserAnswers(savedProgress.progress_data.userAnswers);
            }
            if (savedProgress.progress_data.pronunciationResults) {
              setPronunciationResults(savedProgress.progress_data.pronunciationResults);
            }
          }
        } catch (err) {
          console.error('Error loading progress:', err);
        }
      }
    };

    loadProgress();
  }, [loading, questions.length, topic]);

  const allAnswered = questions.length > 0 && questions.every(q => userAnswers[q.id]);

  // Persist both maps together; progress always carries answers + their scores.
  const persistProgress = async (answers, results) => {
    try {
      await saveExerciseProgress(topic, 'speaking', {
        userAnswers: answers,
        pronunciationResults: results
      });
    } catch (err) {
      console.error('Error saving progress:', err);
    }
  };

  // A finished assessment counts as a completed answer and stores its score.
  const handlePronunciationResult = async (questionId, result) => {
    const updatedAnswers = { ...userAnswers, [questionId]: true };
    const updatedResults = { ...pronunciationResults, [questionId]: result };
    setUserAnswers(updatedAnswers);
    setPronunciationResults(updatedResults);

    await persistProgress(updatedAnswers, updatedResults);
  };

  const submitAnswers = async () => {
    // Stop timer and get final time
    const finalTime = stopTimer();

    // Score = Azure accuracy only, averaged across all assessed sentences (0-100).
    const scores = Object.values(pronunciationResults).map(r => r.accuracy);
    const avgPronunciation = scores.length > 0
      ? Math.round(scores.reduce((sum, s) => sum + s, 0) / scores.length)
      : 0;

    // Record exercise attempt — pronunciation drives the score (out of 100).
    try {
      await recordExerciseAttempt({
        topic: topic,
        exercise_type: 'speaking',
        conversation_id: conversationId,
        score: avgPronunciation,
        max_score: 100,
        time_spent_seconds: finalTime,
        errors: null,
        exercise_ids: questions.map(q => q.id)
      });
    } catch (trackingError) {
      console.error('Failed to record exercise attempt:', trackingError);
    }

    // Reset progress after completion
    try {
      await resetExerciseProgress(topic, 'speaking');
    } catch (err) {
      console.error('Error resetting progress on submit:', err);
    }

    onBack();
  };

  if (loading) return <Loading />;
  if (error) return <Error message={error} />;

  return (
    <ExerciseLayout
      title="Mluveni"
      subtitle={instruction}
      color="blue"
      icon="💬"
      progress={{ current: Object.keys(userAnswers).length, total: questions.length }}
      onBack={onBack}
    >
      <SpeakingPanel
        questions={questions}
        userAnswers={userAnswers}
        onSubmit={submitAnswers}
        allAnswered={allAnswered}
        topic={topic}
        pronunciationResults={pronunciationResults}
        onPronunciationResult={handlePronunciationResult}
      />
    </ExerciseLayout>
  );
});

export default SpeakingExercise;
